import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import { useLanguage } from '../hooks/useLanguage';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const ContactPage = () => {
  useEffect(() => {
    // Force header to stay dark on this page
    const style = document.createElement('style');
    style.innerHTML = `
      .header-dark-wrapper header {
        background-color: #0B1F3F !important;
      }
      .header-dark-wrapper header nav a,
      .header-dark-wrapper header > div > div > div > button {
        color: white !important;
      }
      /* Fix language dropdown visibility - ensure white background and dark text */
      .header-dark-wrapper header .group .absolute div {
        background-color: white !important;
      }
      .header-dark-wrapper header .group .absolute div button {
        background-color: white !important;
        color: #0B1F3F !important;
      }
      .header-dark-wrapper header .group .absolute div button:hover {
        background-color: #f3f4f6 !important;
        color: #0B1F3F !important;
      }
      .header-dark-wrapper header .group .absolute div button.bg-gray-50 {
        background-color: #f9fafb !important;
        color: #0B1F3F !important;
        font-weight: bold !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      navigate('/contact-confirmation');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('contact.errorMessage') || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={`${t('contact.title')} | Wownautic`}
        description={t('location.description')}
        canonicalPath="/contact"
      />
      <div className="header-dark-wrapper">
        <Header />
      </div>
      
      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            
            {/* Contact Info Section */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 lg:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                {t('contact.title')}
              </h2>

              {/* Phone */}
              <div className="flex items-start gap-4 mb-8">
                <div className="flex-shrink-0 w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t('contact.phoneLabel')}</p>
                  <a 
                    href="tel:+34683171717" 
                    className="text-lg font-bold text-gray-900 hover:text-blue-600 transition-colors"
                  >
                    +34 683 17 17 17
                  </a>
                </div>
              </div>

              <div className="h-px bg-blue-200 mb-8"></div>

              {/* Email */}
              <div className="flex items-start gap-4 mb-8">
                <div className="flex-shrink-0 w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t('contact.emailLabel')}</p>
                  <a 
                    href="mailto:info@wownautic.com" 
                    className="text-lg font-bold text-gray-900 hover:text-blue-600 transition-colors break-all"
                  >
                    info@wownautic.com
                  </a>
                </div>
              </div>

              <div className="h-px bg-blue-200 mb-8"></div>

              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-14 h-14 bg-blue-500 rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t('contact.addressLabel')}</p>
                  <p className="text-lg font-bold text-gray-900">
                    Puerto Deportivo Fuengirola, Local 45,<br />
                    Fuengirola, Spain, 29640
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form Section */}
            <div className="bg-white">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                {t('contact.formTitle')}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* First Name & Last Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder={t('contact.firstName')}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder={t('contact.lastName')}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.emailPlaceholder')}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                {/* Subject */}
                <div>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={t('contact.subject')}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                {/* Message */}
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.message')}
                    required
                    rows="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                  ></textarea>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg transition-all transform hover:scale-[1.02] text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (t('contact.sending') || 'Sending...') : t('contact.send')}
                </Button>
              </form>
            </div>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
